import React from "react";
import * as SubframeCore from "@subframe/core";
import { Badge } from "subframe/components/Badge";
import EmployeeGlanceChart from "../../../components/EmployeeGlanceChart/EmployeeGlanceChart";

interface SurveyCategory {
  type: "concern" | "neutral" | "strength";
  title: string;
  badges: string[];
  insufficientData?: boolean;
}

interface SurveyHistoryOverviewProps {
  title: string;
  employeeId: number;
  categories: SurveyCategory[];
  onViewAll: () => void;
  employeeData?: any;
  categoryAnalysisData?: any;
  categoryAnalysisLoading?: boolean;
}

export const SurveyHistoryOverview: React.FC<SurveyHistoryOverviewProps> = ({
  title,
  onViewAll,
  employeeData,
  categoryAnalysisData,
  categoryAnalysisLoading
}) => {
  const formatEmployeeGlanceData = (apiData: any[] = []) => {
    return apiData.map((item) => ({
      triggerDay: item.trigger_day,
      completed: item.completed,
      responseRate: item["Response Rate"],
      overallSentiment: item["Overall Sentiment"],
      sentimentScore: item["Sentiment Score"],
    }));
  };

  return (
    <div className="flex w-full flex-col items-start gap-4 sm:gap-6 rounded-lg bg-default-background px-3 sm:px-6 py-4 sm:py-6 shadow-sm">
      <div className="flex w-full flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
        <span className="text-section-header font-section-header text-default-font">
          {title}
        </span>
        <div className="flex items-center gap-2 cursor-pointer" onClick={onViewAll}>
          <span className="text-subheader font-subheader text-brand-primary">
            View all survey history
          </span>
          <SubframeCore.Icon
            className="text-subheader font-subheader text-brand-primary"
            name="FeatherChevronRight"
          />
        </div>
      </div>
      <div className="flex w-full flex-col lg:flex-row items-stretch gap-4 sm:gap-6">
        <div className="w-full lg:max-w-[576px] lg:grow lg:shrink-0 lg:basis-0 mb-8 md:mb-16">
          <EmployeeGlanceChart
            data={formatEmployeeGlanceData(employeeData?.survey_timeline) as any}
            isLoading={categoryAnalysisLoading}
            showHeading={false}
          />
        </div>
        <div className="flex w-full lg:grow lg:shrink-0 lg:basis-0 flex-col items-start gap-3 sm:gap-4 px-0 sm:px-4">
          {/* Areas of Concern */}
          {categoryAnalysisData?.areas_of_concern && categoryAnalysisData.areas_of_concern.length > 0 && (
            <div className="flex w-full flex-col items-start gap-1">
              <div className="flex w-full items-center gap-2">
                <SubframeCore.Icon
                  className="text-body font-body text-error-600"
                  name="FeatherAlertCircle"
                />
                <span className="text-subheader font-subheader text-error-600">
                  Areas of Concern
                </span>
              </div>
              <div className="flex flex-wrap items-start gap-2">
                {categoryAnalysisData.areas_of_concern.map((cat: any, idx: number) => (
                  <Badge key={idx} variant="neutral">{cat.category}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Neutral Areas */}
          {categoryAnalysisData?.neutral_areas && categoryAnalysisData.neutral_areas.length > 0 && (
            <div className="flex w-full flex-col items-start gap-1">
              <div className="flex w-full items-center gap-2">
                <SubframeCore.Icon
                  className="text-body font-body text-warning-600"
                  name="FeatherAlertTriangle"
                />
                <span className="text-subheader font-subheader text-warning-600">
                  Neutral Areas
                </span>
              </div>
              <div className="flex flex-wrap items-start gap-2">
                {categoryAnalysisData.neutral_areas.map((cat: any, idx: number) => (
                  <Badge key={idx} variant="neutral">{cat.category}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Strengths */}
          {categoryAnalysisData?.strengths && categoryAnalysisData.strengths.length > 0 && (
            <div className="flex w-full flex-col items-start gap-1">
              <div className="flex w-full items-center gap-2">
                <SubframeCore.Icon
                  className="text-body font-body text-success-600"
                  name="FeatherCheckCircle"
                />
                <span className="text-subheader font-subheader text-success-600">
                  Strengths
                </span>
              </div>
              <div className="flex flex-wrap items-start gap-2">
                {categoryAnalysisData.strengths.map((cat: any, idx: number) => (
                  <Badge key={idx} variant="neutral">{cat.category}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 